/**
 * This is a SearchL Component.This Component is used to render the Search and the Auto Suggest Component on L Screen. This Component will open the Search Suggestion on click of Search Icon.
 *
 * @module views/components/SearchL
 * @memberof -Common
 */
import './SearchL.scss';

import React, { useCallback, useEffect, useRef, useState } from 'react';

import { FocusScope } from '@react-aria/focus';
import classNames from 'classnames';
import { Form, Formik } from 'formik';

import Button from '@ulta/core/components/Button/Button';
import InputFieldUnderline from '@ulta/core/components/InputFieldUnderline/InputFieldUnderline';
import UseKeyPress from '@ulta/core/hooks/useKeyPress/UseKeyPress';
import { useKeyPressCounter } from '@ulta/core/hooks/useKeyPressCounter/useKeyPressCounter';
import useOutsideClick from '@ulta/core/hooks/useOutsideClick/useOutsideClick';
import { OVERLAY_SCROLL_BLOCK_CLASS } from '@ulta/core/providers/OverlayProvider/OverlayProvider';
import constants from '@ulta/core/utils/constants/constants';
import { spreadProps } from '@ulta/core/utils/dom/dom';

import { useSearchHeaderContext } from '@ulta/providers/SearchHeaderProvider/SearchHeaderProvider';

import AutoSuggestDesktop from '../AutoSuggestDesktop/AutoSuggestDesktop';
import * as utils from './SearchL';
/**
  * Represents a SearchL component
  *
  * @method
  * @returns SearchL
  */
export const SearchL = function(){
  const [showAutoSuggest, setShowAutoSuggest] = useState( false );
  const wrapperRef = useRef();
  const inputRef = useRef();

  const {
    ariaLabel,
    screenReaderText,
    clearAccessibilityLabel,
    closePanel,
    handleSubmit,
    inputChangeHandler,
    invokeTypeAheadAction,
    loading,
    resetAction,
    moveCursorToEndOfInput,
    savedSearchTerm,
    searchInputLabel,
    setKeyBoardUser,
    submitSearchLabel,
    suggestions
  } = useSearchHeaderContext();

  const displayPanel = suggestions?.length > 0;

  // Close panel on outside click
  useOutsideClick( { watcher: showAutoSuggest, el: wrapperRef?.current }, { handleClick: () => {
    setShowAutoSuggest( false );
  } } );

  // Close panel on ESC key
  UseKeyPress( [constants.ESC_KEY], wrapperRef, () => {
    setShowAutoSuggest( false );
  }, [constants.ESC_KEY], false );

  // Activate ADA mode after 3 tabs detected
  useKeyPressCounter( { keyCodes: [constants.TAB_KEY], threshold: 3 }, { onThreshold: () => {
    setKeyBoardUser( true );
  } } );

  useEffect( () => {
    if( showAutoSuggest && displayPanel ){
      global.document.body.classList.add( OVERLAY_SCROLL_BLOCK_CLASS );
    }
    else {
      global.document.body.classList.remove( OVERLAY_SCROLL_BLOCK_CLASS );
    }

    return () => {
      global.document.body.classList.remove( OVERLAY_SCROLL_BLOCK_CLASS );
    };
  }, [showAutoSuggest, suggestions] );

  // On Submit Handler
  const onSubmitHandler = useCallback( utils.composeOnSubmit( { inputRef }, { handleSubmit, setShowAutoSuggest } ) );

  return (
    <div className='SearchL'
      ref={ wrapperRef }
      { ...spreadProps( {
        'role': showAutoSuggest ? 'region' : undefined,
        'aria-describedby': showAutoSuggest ? 'searchDescription' : undefined
      } ) }
    >
      <div className='SearchL__searchIcon'>
        <Button
          variant='tertiary'
          icon
          iconImage='search'
          iconSize='xl'
          ariaLabel={ ariaLabel }
          ariaHiddenIcon={ true }
          onClick={ () => {
            invokeTypeAheadAction( savedSearchTerm );
            setShowAutoSuggest( true );
          } }
          className='SearchL--button'
          ariaControls='search-content'
          isExpanded={ showAutoSuggest }
        />
      </div>

      { showAutoSuggest && (
        <FocusScope
          contain={ showAutoSuggest }
          group='searchPanel'
          restoreFocus={ true }

          autoFocus={ true }
        >
          { showAutoSuggest && displayPanel && (
            <div
              className='SearchL__overlay'
            />
          ) }
          <div open={ showAutoSuggest }
            className='SearchL__searchBar'
            id='search-content'
          >
            <Formik
              initialValues={ { search: savedSearchTerm } }
              onSubmit={ onSubmitHandler }
            >
              { () => (
                <Form className={ classNames( 'SearchL__form', {
                  'SearchL__form--loading': loading
                } ) }
                role='search'
                >
                  <p className='sr-only'
                    id='searchDescription'
                  >
                    { screenReaderText }
                  </p>
                  <InputFieldUnderline
                    ariaDescribedBy='searchDescription'
                    ref={ inputRef }
                    type='search'
                    name='search'
                    autoComplete='off'
                    leftIcon='Search'
                    onChange={ inputChangeHandler }
                    handleFocus={ () => {
                      moveCursorToEndOfInput( inputRef );
                    } }
                    handleResetVal={ resetAction }
                    placeholder={ searchInputLabel }
                    displayGoButton={ true }
                    clearValueText={ clearAccessibilityLabel }
                    submitText={ submitSearchLabel }
                  />
                </Form>
              ) }
            </Formik>
            <form
              className='SearchL__closeContainer'
            >
              <Button
                icon
                variant='unstyled'
                iconImage='X'
                iconSize='lg'
                ariaLabel={ closePanel }
                className='SearchL__searchBarClose'
                onClick={ () => {
                  setShowAutoSuggest( false );
                } }
                type='submit'
              />
            </form>
            { displayPanel && <AutoSuggestDesktop /> }

          </div>
        </FocusScope>
      ) }
    </div>
  );
};

/**
 * Method to handle searchPanel close event
 * @param {object} data args
 * @param {string} data.inputRef input value
 * @param {object} methods args
 * @param {function} methods.handleSubmit handles submit from SearchHeader
 * @param {function} methods.closeOverlay modal on close handler
 * @param {function} values args from Formik form
*/
export const composeOnSubmit = ( data, methods ) => ( values ) => {
  const { inputRef } = data || {};
  const { handleSubmit, setShowAutoSuggest } = methods || {};

  if( !inputRef?.current || !handleSubmit || !setShowAutoSuggest ){
    return;
  }

  handleSubmit( values, inputRef );
  setTimeout( () => {
    setShowAutoSuggest( false );
  }, 0 );
};

export default SearchL;
